import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { BrandFeature, TopNavMenuItem } from '@/brands';
import { brand, isMenuLinkPainted, useBrandFeatures, useBrandMenus } from '@/brands';

// Same three filters as the desktop `Navbar`, on the same ids: `build` is what
// this build ships, `grant` is the super admin's Feature for the brand, `menu`
// is their show/hide for this one link. All three must pass, so revoking Casino
// in the panel drops it from both bars at once — SPORTS is the mobile wording
// for the same `home` entry. The menu half goes through `isMenuLinkPainted` for
// the same reason it does on desktop: a page one of its other entries closed
// must not keep a button here.
type MenuItem = {
  name: string;
  path: string;
  build?: keyof typeof brand.features;
  grant?: BrandFeature;
  menu: TopNavMenuItem;
};

const allMenuItems: MenuItem[] = [
  { name: 'SPORTS', path: '/home', menu: 'home' },
  { name: 'CASINO', path: '/casino/all', build: 'aggregator', grant: 'casino', menu: 'casino' },
  { name: 'SLOTS', path: '/casino/all/slots', build: 'aggregator', grant: 'casino', menu: 'slots' },
  { name: 'LIVE CASINO', path: '/casino/all/live', build: 'aggregator', grant: 'casino', menu: 'liveCasino' },
  { name: 'TABLE GAMES', path: '/casino/all/table', build: 'aggregator', grant: 'casino', menu: 'tableGames' },
  { name: 'CRASH', path: '/casino/all/crash', build: 'aggregator', grant: 'casino', menu: 'crash' },
  { name: 'LOTTERY', path: '/casino/all/lottery', build: 'aggregator', grant: 'lottery', menu: 'lottery' },
];

// Build-time half of the filter; the grant and menu halves need GET /brand, so
// they run in the component below.
const buildMenuItems = allMenuItems.filter(
  (item) => !item.build || brand.features[item.build]
);

const MobileNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const activeRef = useRef<HTMLButtonElement | null>(null);
  const menus = useBrandMenus();
  const features = useBrandFeatures();
  const menuItems = buildMenuItems.filter(
    (item) =>
      (!item.grant || features[item.grant] !== false) &&
      isMenuLinkPainted(item.path, menus.topNav[item.menu] !== false, menus)
  );

  useEffect(() => {
    activeRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [location.pathname]);

  return (
    <nav className="xl:hidden mob-nav">
      <style>{`
        .mob-nav {
          /* brand primary — set by applyBrandTheme() */
          background: var(--brand-primary, #0088cc);
          border-bottom: 1px solid rgba(0,0,0,0.15);
        }
        .mob-nav-list {
          display: flex;
          align-items: stretch;
          width: 100%;
          margin: 0;
          padding: 0 4px;
          gap: 4px;                   /* keep a clear gap so tabs never touch */
          list-style: none;
          /* Horizontal slider — tabs keep their natural width and scroll */
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }
        .mob-nav-list::-webkit-scrollbar { display: none; }
        .mob-nav-item {
          flex: 0 0 auto;             /* natural width, no shrink — enables the slider */
          min-width: 60px;
          height: 43px;
          margin: 0;
          padding: 0;
          list-style: none;
          border-right: 1px solid rgba(255,255,255,0.55);   /* white divider after each tab */
        }
        .mob-nav-item:last-child { border-right: none; }
        .mob-nav-link {
          box-sizing: border-box;
          display: flex;
          align-items: center;        /* vertical center */
          justify-content: center;
          gap: 4px;                   /* 4px spacing for icon/text */
          width: 100%;
          height: 100%;
          padding: 2px 4px;           /* same padding on every tab */
          font-family: 'Roboto Condensed', sans-serif;
          font-size: 12px;
          font-weight: 700;
          line-height: 1.1;
          color: #ffffff;
          text-transform: uppercase;
          letter-spacing: 0.2px;
          text-align: center;
          white-space: nowrap;        /* single line — labels never wrap in the slider */
          background: transparent;
          border: none;
          border-top: 3px solid transparent;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;   /* no tap flash/overlay on mobile */
          transition: color .15s, background .15s, border-color .15s;
        }
        .mob-nav-link:hover { color: #fff; }
        .mob-nav-link.active {
          color: #fff;
          background: transparent;              /* no overlay highlight */
          border-top-color: #9ca3af;            /* gray top border only */
        }
        .mob-nav-ico {
          height: 14px;
          width: auto;
          flex-shrink: 0;
        }
        /* Smooth idle fade: bright red → darker/dim → bright red, looping.
           Only opacity + brightness animate, so size/position/sharpness stay intact. */
        .mob-nav-ico.crash-pulse {
          width: 43px;
          height: 30px;
          object-fit: contain;        /* keep aspect + sharpness within 43×30 */
          filter: brightness(1) saturate(1.3);
          animation: redIdleFade 3.2s ease-in-out infinite;
          will-change: opacity, filter;
        }
        @keyframes redIdleFade {
          0%   { opacity: 1;    filter: brightness(1) saturate(1.3); }
          50%  { opacity: 0.5;  filter: brightness(0.42) saturate(1.3); }
          100% { opacity: 1;    filter: brightness(1) saturate(1.3); }
        }
        @media (prefers-reduced-motion: reduce) {
          .mob-nav-ico.crash-pulse { animation: none; }
        }
      `}</style>

      <ul className="mob-nav-list">
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;

          return (
            <li key={item.name} className="mob-nav-item">
              <button
                ref={isActive ? activeRef : null}
                onClick={() => navigate(item.path)}
                className={`mob-nav-link${isActive ? ' active' : ''}`}
              >
                {item.name === 'CRASH' && (
                  <img
                    src="/icons/aviator.png"
                    alt="Crash"
                    className="mob-nav-ico crash-pulse"
                    loading="eager"
                    decoding="sync"
                  />
                )}
                {item.name}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default MobileNavbar;
